import i18n from "i18next";
import { ID_PREFIX_BOX } from "../../../common/constants";
import { createBox } from "../../../common/common_tools";

/**
 * Default scene-header text box.
 *
 * Replaces the legacy in-canvas EditorHeader (removed in commits fe49f6e4 +
 * e4144bdb). When a new scene is created from the carousel "+" button or from
 * the empty-template branch of TemplatesModal, drop a single editable
 * BasicText box at the top of the scene as a default title — placed where the
 * old header used to render so users see a familiar layout, but as a normal
 * deletable / editable element this time.
 *
 * Position + size are taken from the design decision A:
 *   x: 5%, y: 5%, width: 90%, height: 10%
 * (close to the legacy header's "full top band" feel without touching the
 * canvas edges, and tolerable in any aspect ratio).
 *
 * Skip the default header when the chosen template already ships its own
 * heading box — TemplatesModal's `template !== -1` branch handles that.
 */
export function buildDefaultSceneHeaderInitialParams(navItemId, idSuffix = "h") {
  const placeholder = i18n.t("templates.scene_header_placeholder") || "Scene Title";
  return {
    id: ID_PREFIX_BOX + Date.now() + "_" + idSuffix,
    parent: navItemId,
    container: 0,
    col: 0,
    row: 0,
    width: "90%",
    height: "10%",
    position: { x: "5%", y: "5%", type: "absolute" },
    name: "BasicText",
    isDefaultPlugin: true,
    page: navItemId,
    initialState: {
      __text: "<h1 class='no_margins'>" + placeholder + "</h1>",
    },
  };
}

/**
 * Convenience helper that calls createBox with the default-header initial
 * params. Keeps both call-sites (TemplatesModal + EditorApp post-hydrate)
 * symmetrical.
 */
export function addDefaultSceneHeader({ navItemId, onBoxAdded, boxes, idSuffix }) {
  if (!navItemId || !onBoxAdded) return;
  const initialParams = buildDefaultSceneHeaderInitialParams(navItemId, idSuffix);
  createBox(initialParams, "BasicText", true, onBoxAdded, boxes || {});
}

/* =============================================================
   Legacy scene header → editable BasicText box (one-time migration)
   ============================================================= */

// Old _editorHeader.scss palette + sizes — preserved verbatim so migrated
// boxes look identical to the legacy in-canvas header.
//   .title .cab             font-size: 2em;          (root 16px → 32px)
//   h1 (courseTitle)        font-size: 1em            (= 32px)
//   h2 (documentTitle)      font-size: 0.8em; color #a963f5; weight 600
//   h3 (documentSubtitle)   font-size: 0.7em; color #888
//   .cabtabla_numero        color #a963f5; font-family Ubuntu
const LEGACY_BLUEPRIMARY = "#a963f5";
const LEGACY_GRISADAMS = "#888";

function escapeHtml(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isVisibleMode(value) {
  return value === "expanded" || value === "reduced";
}

/**
 * Build a BasicText box payload that mirrors the visible parts of the legacy
 * EditorHeader for one scene. Returns null when the scene has no visible
 * heading content to migrate (e.g. all fields hidden / empty).
 *
 * The box is positioned and sized exactly like the new default header
 * (5%, 5%, 90%, auto-height) so any text it contains lands where the legacy
 * header used to render. Old fonts/colors/sizes are preserved verbatim via
 * inline styles so the migrated box visually matches the previous output.
 */
export function buildLegacyHeaderMigrationParams(scene, viewToolbar) {
  if (!scene || !viewToolbar) return null;

  const docTitle = isVisibleMode(viewToolbar.documentTitle)
    ? viewToolbar.documentTitleContent || ""
    : "";
  const subTitle = isVisibleMode(viewToolbar.documentSubtitle)
    ? viewToolbar.documentSubtitleContent || ""
    : "";
  const pageNum =
    viewToolbar.numPage === "expanded" && viewToolbar.numPageContent
      ? String(viewToolbar.numPageContent)
      : "";

  const docTitleClean = (docTitle || "").trim();
  const subTitleClean = (subTitle || "").trim();
  if (!docTitleClean && !subTitleClean && !pageNum) return null;

  const parts = [];
  if (pageNum) {
    parts.push(
      '<div style="display:inline-block;padding:0.2em 0.3em;line-height:1em;color:' +
        LEGACY_BLUEPRIMARY +
        ";font-family:'Ubuntu',sans-serif\">" +
        escapeHtml(pageNum) +
        "</div>"
    );
  }
  if (docTitleClean) {
    parts.push(
      '<h2 style="margin:0.4em 0 0;font-size:1.6em;color:' +
        LEGACY_BLUEPRIMARY +
        ';font-weight:600">' +
        escapeHtml(docTitleClean) +
        "</h2>"
    );
  }
  if (subTitleClean) {
    parts.push(
      '<h3 style="margin:0.4em 0 0;font-size:1.4em;color:' +
        LEGACY_GRISADAMS +
        '">' +
        escapeHtml(subTitleClean) +
        "</h3>"
    );
  }

  return {
    id: ID_PREFIX_BOX + Date.now() + "_legacyhdr_" + scene.id,
    parent: scene.id,
    container: 0,
    col: 0,
    row: 0,
    width: "90%",
    height: "auto",
    position: { x: "5%", y: "5%", type: "absolute" },
    name: "BasicText",
    isDefaultPlugin: true,
    page: scene.id,
    initialState: {
      __text: parts.join(""),
    },
  };
}

/**
 * Walk every scene in the loaded state, materialise the legacy header text
 * into a real BasicText box, and hide the legacy header fields on the
 * matching viewToolbar so the data converges to the new format. Idempotent
 * via globalConfig.legacyHeadersMigrated — runs at most once per training.
 *
 * @returns {boolean} true when migration ran (at least one scene migrated or
 *   the marker was flipped), false when nothing to do.
 */
export function migrateLegacySceneHeaders({
  present,
  dispatch,
  addBoxAction,
  updateViewToolbarAction,
  changeGlobalConfigAction,
}) {
  if (!present || !dispatch) return false;
  if (!addBoxAction || !updateViewToolbarAction || !changeGlobalConfigAction) return false;

  const globalConfig = present.globalConfig || {};
  if (globalConfig.legacyHeadersMigrated === true) return false;

  const navItemsById = present.navItemsById || {};
  const viewToolbarsById = present.viewToolbarsById || {};
  const boxesById = present.boxesById || {};

  let migratedAny = false;
  Object.values(navItemsById).forEach((scene) => {
    if (!scene || !scene.id || scene.id === 0) return;
    if (scene.type !== "slide" && scene.type !== "document") return;
    const viewToolbar = viewToolbarsById[scene.id];
    if (!viewToolbar) return;

    const params = buildLegacyHeaderMigrationParams(scene, viewToolbar);
    if (!params) return;

    createBox(
      params,
      "BasicText",
      scene.type === "slide",
      (...args) => dispatch(addBoxAction(...args)),
      boxesById
    );

    dispatch(
      updateViewToolbarAction(scene.id, {
        documentTitle: "hidden",
        documentSubtitle: "hidden",
        numPage: "hidden",
        courseTitle: "hidden",
      })
    );

    migratedAny = true;
  });

  // Flip the marker even if nothing was migrated — the training has been
  // observed once and we don't need to re-scan on every refresh.
  dispatch(changeGlobalConfigAction("legacyHeadersMigrated", true));
  return migratedAny;
}
